import React, { useEffect, useState } from 'react';
// Routes
import {
	NavLink,
	useNavigate,
	useLocation,
	useSearchParams
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// Color
// MUI
import {
	AppBar,
	Box,
	CssBaseline,
	Toolbar,
	Typography,
	Avatar,
	IconButton,
	Paper,
	useMediaQuery,
	Grid2 as Grid,
	Button,
	ButtonGroup
} from '@mui/material';
import { Logout, Settings } from '@mui/icons-material';
import SyncLockIcon from '@mui/icons-material/SyncLock';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DrawIcon from '@mui/icons-material/Draw';
import { Stack } from '@mui/system';
import PropTypes from 'prop-types';
import PrimaryButton from '../Common/Button/primary-button.js';
import {
	BLACK,
	GREEN,
	SALT_PAN,
	TERTIARY_GREY,
	SUVA_GREY
} from '../../Assets/Constants/theme.js';
// Image
import LeftArrow from '../../Assets/Images/LeftArrow.svg';
import EditButton from '../../Assets/Images/edit.svg';
import Logo from '../../Assets/Images/svgs/Logo.svg';
// Page
import CreateNewTemplate from '../Pages/CKEditorComponent/CKEditorpopup/create-new-template.js';
import CKTemplatePopup from '../Pages/CKEditorComponent/CKEditorpopup/cktemplate-popup.js';
import { useQueryParameters } from '../Common/use-query-parameters.js';
import {
	resetDashboard,
	updateDigitalStampStatus,
	updateEsignStatus
} from '../../Store/DashboardSlice/dashboard-slice.js';
import { getProfilePic, userLogout } from '../../Services/api-config.js';
import {
	openWarning,
	updateStatus,
	updateUserInfo
} from '../../Store/AuthSlice/auth-slice.js';
import RenderMenu from '../Common/MenuDropdown/render-menu.js';
import { getErrorMessage, handleLogoutUtil } from '../../utils/util.js';
import DigitalStampOrder from '../Common/DigitalStampOrderPopup/digital-stamp-order-popup.js';
import permissions from '../../utils/permissions.js';
import customToast from '../Common/custom-toast.js';
import Loading from '../Common/LoaderComponent/loader.js';
import { routeConstants } from '../../utils/path-constants.js';
import useAccountType from '../Hooks/use-account-type.js';

const commonStyles = {
	textTransform: 'none',
	fontWeight: 500,
	padding: '8px 16px',
	minWidth: '100px',
	transition: 'all 0.3s ease',
	border: 'none',
	boxShadow: 'none'
};

const Header = ({ menuItems }) => {
	const accountType = useAccountType();
	const [searchParams, setSearchParams] = useSearchParams();
	const documentId = searchParams.get('documentId');
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const isMobile = useMediaQuery('(max-width:840px)');
	const { viewEsignStats, viewDigitalStampStats } = permissions();
	const { ckButton } = useSelector((state) => state.ckeditor);
	const { holdTemplateName } = useSelector((state) => state.fileList);
	const { digitalStampOrderStatus, esignStatus } = useSelector(
		(state) => state.dashboard
	);
	const { showToast } = customToast();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { TemplateEngineAdd } = permissions();

	const [isCKTemplate, setIsCKTemplate] = useState(false);

	const [navItems, setNavItems] = useState([]);
	const { userInfo, redirectRoute } = useSelector((state) => state.auth);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);
	const [name, setName] = useState('');
	const { getParameter } = useQueryParameters();
	const createmode = getParameter('createmode');

	const [openDigitalOrderPopup, setOpenDigitalOrderPopup] = useState(false);

	const [profileImage, setProfileImage] = useState('');
	const [brandName, setBrandName] = useState(menuItems?.branding?.brandName);
	const [brandLogo, setBrandLogo] = useState(menuItems?.branding?.brandLogo);

	const initialItemMenu = [
		{
			title: 'Profile',
			icon: AccountCircleIcon,
			onClick: () => navigate(routeConstants.PROFILE),
			fontSize: 'small'
		},
		{
			title: 'Signature',
			icon: DrawIcon,
			onClick: () => navigate(routeConstants.SIGNATURE),
			fontSize: 'small'
		},
		{
			title: 'Settings',
			icon: Settings,
			onClick: () => navigate('/setting'),
			fontSize: 'small'
		},
		{
			title: 'Change Password',
			icon: SyncLockIcon,
			onClick: () => handleResetPassword(),
			fontSize: 'small'
		},
		{
			title: 'Logout',
			icon: Logout,
			onClick: () => handleLogout(),
			fontSize: 'small'
		}
	];

	const [isBusiness, setIsBusiness] = useState(accountType !== 'individual');

	const initialMenu = () => {
		if (isBusiness) {
			return initialItemMenu.filter((item) => item.title !== 'Signature');
		}

		return initialItemMenu.filter((item) => item.title !== 'Settings');
	};

	const [itemMenu, setItemMenu] = useState(initialMenu());

	useEffect(() => {
		setBrandName(menuItems?.branding?.brandName);
		setBrandLogo(menuItems?.branding?.brandLogo);
	}, [menuItems?.branding?.brandName, menuItems?.branding?.brandLogo]);

	const handleResetPassword = () => {
		navigate('/reset-password-profile');
	};

	const handleTabChange = (event, newValue) => {
		if (location.pathname === '/new-esign' && documentId) {
			searchParams.append('approval', true);
			setSearchParams(searchParams);
		} else if (location.pathname === '/edit') {
			dispatch(
				updateStatus({
					value: true,
					data: { event, saveDraft: true }
				})
			);
		} else if (newValue === 1) {
			if (!isBusiness) {
				return;
			}

			const filtered = initialItemMenu.filter((item) => item.title !== 'Settings');
			setItemMenu(filtered);
			setIsBusiness(false);
			navigate(routeConstants.ACTION_REQUIRED);
		} else if (redirectRoute) {
			if (isBusiness) {
				return;
			}

			const filtered = initialItemMenu.filter((item) => item.title !== 'Signature');
			setItemMenu(filtered);
			setIsBusiness(true);
			navigate(redirectRoute);
		}
	};

	const handleClick = (event) => {
		const preventDefaultPaths = [
			'/recipient',
			'/stamp-form',
			'/edit',
			'/templatepreview'
		];
		if (location.pathname === '/new-esign' && documentId) {
			event.preventDefault();
			searchParams.append('approval', true);
			setSearchParams(searchParams);
			return;
		}

		if (preventDefaultPaths.includes(location.pathname)) {
			event.preventDefault();
			dispatch(updateStatus(true));
		} else {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	useEffect(() => {
		if (userInfo?.profileImageChanged) {
			fetchProfilePic();
			dispatch(
				updateUserInfo({
					profileImageChanged: false
				})
			);
		}
	}, [userInfo?.profileImageChanged]);

	const fetchProfilePic = async () => {
		const response = await getProfilePic();
		if (response?.status === 200) {
			setProfileImage(response?.data?.base64Image);
			formatHeaderName(
				`${response?.data?.firstName} ${response?.data?.lastName}`
			);
		}
	};

	function shouldShowEsign(tempTopMenu, userInfo) {
		return (
			tempTopMenu
			&& Object.keys(userInfo?.menu?.Dashboard || {}).includes('esign')
		);
	}

	function shouldShowBulkEsign(tempTopMenu, userInfo) {
		return (
			tempTopMenu
			&& Object.keys(userInfo?.menu?.Dashboard || {}).includes('bulk_esign')
		);
	}

	function shouldShowDigitalStamp(tempTopMenu, userInfo) {
		return (
			tempTopMenu
			&& Object.keys(userInfo?.menu?.Dashboard || {}).includes('digital_stamp')
		);
	}

	useEffect(() => {
		const temp = [];
		if (userInfo?.menu) {
			const menuItem = Object.keys(userInfo?.menu);
			const tempTopMenu = menuItem.includes('Dashboard');
			if (shouldShowEsign(tempTopMenu, userInfo)) {
				const tempArr = userInfo?.menu?.Dashboard.esign;
				const isVisible = tempArr.some(
					(val) => val.permissionName === 'new_esign'
				);
				isVisible
					&& temp.push({
						id: 1,
						title: '+ New e-Sign',
						color: GREEN,
						navigateTo: '/uploadfile'
					});
			}

			if (shouldShowBulkEsign(tempTopMenu, userInfo)) {
				const tempArr = userInfo?.menu?.Dashboard.bulk_esign;
				const isVisible = tempArr.some(
					(val) => val.permissionName === 'bulk_new_esign'
				);
				if (isVisible) {
					dispatch(updateEsignStatus(true));
				} else {
					dispatch(updateEsignStatus(false));
				}
			}

			if (shouldShowDigitalStamp(tempTopMenu, userInfo)) {
				const tempArrOrg = userInfo?.menu?.Dashboard.digital_stamp;
				if (
					tempArrOrg.some(
						(val) => val.permissionName === 'digital_stamp_org_request'
					)
				) {
					dispatch(updateDigitalStampStatus(true));
				} else {
					dispatch(updateDigitalStampStatus(false));
				}
			}

			temp.push(
				{
					id: 2,
					title: 'Notification',
					color: SALT_PAN,
					navigateTo: ''
				},
				{
					id: 2,
					title: 'Profile',
					color: SALT_PAN,
					navigateTo: '/profile'
				}
			);

			if (!menuItem.includes('Settings')) {
				const temp = itemMenu.filter((val) => val.title !== 'Settings');
				setItemMenu(temp);
			}
		} else {
			temp.push({
				id: 2,
				title: 'Profile',
				color: SALT_PAN,
				navigateTo: '/profile'
			});
		}

		setNavItems(temp);
	}, [userInfo]);

	function formatHeaderName(userName = '') {
		const splitWords = userName?.split(' ');
		const firstLetter = splitWords[0].charAt(0).toUpperCase();
		if (splitWords.length === 1) {
			setName(firstLetter);
		} else {
			const lastLetter = splitWords.at(-1).charAt(0).toLowerCase();

			setName(firstLetter + lastLetter.toUpperCase());
		}
	}

	const handleLogout = async () => {
		try {
			setIsLoading(true);
			dispatch(resetDashboard());
			const response = await userLogout();
			if (response?.status === 200) {
				handleLogoutUtil(dispatch, navigate);
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const headerLogos = [{}];

	const handleBackBtn = () => {
		if (window.location.pathname === '/templatepreview') {
			navigate(-1);
		} else {
			dispatch(openWarning());
		}
	};

	return (
		<>
			<Loading open={isLoading} />
			{isMobile && (
				<Box>
					<RenderMenu
						anchorEl={anchorEl}
						open={open}
						handleClose={handleClose}
						handleLogout={handleLogout}
						handleResetPassword={handleResetPassword}
						itemMenu={itemMenu}
					/>
					<Grid container sx={{ width: '100%' }}>
						<Paper
							elevation={0}
							sx={{
								width: '100%',
								position: 'fixed',
								top: 0,
								left: 0,
								right: 0,
								bgcolor: '#FFF',
								zIndex: 99
							}}
						>
							<Grid
								container
								justifyContent={'space-between'}
								alignItems='center'
								padding='14px'
								overflow={'auto'}
								flexWrap={'nowrap'}
								columnSpacing={2}
							>
								<Grid>
									<Stack direction={'row'} alignItems={'center'} columnGap={2}>
										<NavLink
											style={({ isActive }) => ({
												stroke: isActive ? TERTIARY_GREY : SUVA_GREY,
												pointerEvents: accountType === 'individual' || (!viewEsignStats && !viewDigitalStampStats)
													? 'none'
													: 'auto',
												cursor: accountType === 'individual' || (!viewEsignStats && !viewDigitalStampStats)
													? 'not-allowed'
													: 'pointer'
											})}
											to={'/statistics'}
											onClick={(event) => {
												if (
													accountType === 'individual'
			|| (!viewEsignStats && !viewDigitalStampStats)
												) {
													event.preventDefault(); // Extra safety
													return;
												}

												if (location.pathname === '/new-esign' && documentId) {
													event.preventDefault();
													searchParams.append('approval', true);
													setSearchParams(searchParams);
												}
											}}
										>
											<img style={{ verticalAlign: 'middle' }} src={Logo} alt='' />
										</NavLink>

										<Box>
											{menuItems?.branding?.brandName ? (
												<Box
													className='medium4'
													sx={{
														display: 'flex',
														width: '130px',
														height: '40px',
														backgroundColor: '#ECF0E5',
														borderRadius: '5px',
														alignItems: 'center',
														justifyContent: 'center',
														cursor: 'pointer'
													}}
												>
													{brandName}
												</Box>
											) : (
												menuItems?.branding?.brandLogo && (
													<img
														alt='avatar'
														src={brandLogo || null}
														className='avatar'
														style={{
															height: '40px',
															border: '2px solid #B7D423',
															position: 'relative'
														}}
													/>
												)
											)}
										</Box>
									</Stack>
								</Grid>

								<Grid>
									<Stack direction={'row'} alignItems={'center'} columnGap={2}>
										{headerLogos.map((val) => (
											<img
												key={val.id}
												style={{ verticalAlign: 'middle' }}
												src={val.image}
												alt={val?.id}
											/>
										))}
										<Box display='flex' alignItems='center' gap={2}>
											{/* Button Group on the left */}
											<ButtonGroup
												sx={{
													borderRadius: '10px',
													overflow: 'hidden',
													border: '1px solid #b7d423',
													width: '125px'
												}}
											>
												<Button
													onClick={(event) => {
														handleTabChange(event, 1);
													}}
													sx={{
														...commonStyles,
														backgroundColor: isBusiness ? '#fff' : '#b7d423',
														color: 'black',
														fontSize: '10px'
													}}
												>
      Individual
												</Button>
												<Button
													onClick={(event) => {
														handleTabChange(event, 0);
													}}
													sx={{
														...commonStyles,
														backgroundColor: isBusiness ? '#b7d423' : '#fff',
														color: 'black',
														fontSize: '10px'
													}}
												>
      Business
												</Button>
											</ButtonGroup>
											{/* Avatar or Name Display */}
											<IconButton sx={{ p: 0 }}>
												{profileImage ? (
													<Avatar
														src={profileImage || null}
														className='avatar'
														onClick={handleClick}
														sx={{
															width: '40px',
															height: '40px',
															border: '2px solid #B7D423',
															position: 'relative'
														}}
													/>
												) : (
													<Box
														className='semiBold7'
														onClick={handleClick}
														style={{
															width: '40px',
															height: '40px',
															borderRadius: '20px',
															border: '2px solid #B7D423',
															display: 'flex',
															justifyContent: 'center',
															alignItems: 'center',
															position: 'relative'
														}}
													>
														{name}
													</Box>
												)}
											</IconButton>
										</Box>
									</Stack>
								</Grid>
							</Grid>
						</Paper>
					</Grid>
				</Box>
			)}
			{!isMobile && (
				<Box
					sx={{
						display: 'flex',
						width: { sm: '93%', lg: '95%' },
						position: 'fixed',
						top: 0,
						zIndex: '99'
					}}
				>
					<CssBaseline />
					<AppBar
						position='static'
						sx={{
							width: '100%',
							backgroundColor: '#fff',
							boxShadow: 0,
							borderBottom: '1px solid #ECF0E5'
						}}
					>
						<Toolbar
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center'
							}}
						>
							{ckButton ? (
								<div
									style={{
										color: BLACK,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										gap: '10px'
									}}
								>
									<Box
										component={'img'}
										src={LeftArrow}
										alt=''
										onClick={handleBackBtn}
										style={{ cursor: 'pointer' }}
									/>

									<div
										style={{
											fontSize: '12px',
											display: 'flex',
											alignItems: 'center'
										}}
									>
										<Typography style={{ paddingRight: '10px' }}>
											{' '}
											{holdTemplateName}
										</Typography>
										{createmode === 'true' && (
											<Box
												component={'img'}
												src={EditButton}
												onClick={() => setIsPopupOpen(true)}
											/>
										)}
										<CKTemplatePopup
											open={isPopupOpen}
											onClose={() => setIsPopupOpen(!isPopupOpen)}
											type={'edit'}
										/>
									</div>
								</div>
							) : (
								<Typography
									variant='h6'
									component='div'
									sx={{ flexGrow: 1, display: { color: '#21241E' } }}
								>
									Hello, {userInfo?.userName}
								</Typography>
							)}

							<Box sx={{ display: 'flex', gap: 4 }}>

								{accountType !== 'individual' && ckButton && createmode === 'true' && TemplateEngineAdd && (
									<PrimaryButton
										value={'Create New Template'}
										backgroundColor={GREEN}
										borderRadius={'8px'}
										fontWeight={400}
										padding='10px 16px'
										textcolor={'#000'}
										onClick={() => setIsCKTemplate(true)}
									/>
								)}
								{accountType !== 'individual' && esignStatus && (
									<PrimaryButton
										className={'semiBold14'}
										value={'Bulk e-Sign'}
										backgroundColor={GREEN}
										borderRadius={'8px'}
										fontWeight={400}
										padding='10px 16px'
										textcolor={'#000'}
										onClick={(event) => {
											if (location.pathname === '/new-esign' && documentId) {
												searchParams.append('approval', true);
												setSearchParams(searchParams);
											} else if (location.pathname === '/edit') {
												dispatch(
													updateStatus({
														value: true,
														data: { event, saveDraft: true }
													})
												);
											} else {
												navigate('/bulk-esign');
											}
										}}
									/>
								)}

								{accountType !== 'individual' && digitalStampOrderStatus && (
									<PrimaryButton
										className={'semiBold14'}
										value={'+ Digital Stamp Order'}
										backgroundColor={GREEN}
										borderRadius={'8px'}
										fontWeight={400}
										padding='10px 16px'
										textcolor={'#000'}
										onClick={(event) => {
											if (location.pathname === '/new-esign' && documentId) {
												searchParams.append('approval', true);
												setSearchParams(searchParams);
											} else if (location.pathname === '/edit') {
												dispatch(
													updateStatus({
														value: true,
														data: { event, saveDraft: true }
													})
												);
											} else {
												setOpenDigitalOrderPopup(true);
											}
										}}
									/>
								)}
								{isCKTemplate && (
									<CreateNewTemplate
										open={isCKTemplate}
										onClose={() => setIsCKTemplate(false)}
									/>
								)}
								{accountType !== 'individual' && navItems.map((item) => {
									if (
										item.title !== 'Profile'
										&& item.title !== 'Notification'
									) {
										return (
											<PrimaryButton
												className={'semiBold14'}
												value={item.title}
												backgroundColor={item.color}
												borderRadius={'8px'}
												fontWeight={400}
												padding='10px 16px'
												key={item.id}
												textcolor={'#000000'}
												onClick={(event) => {
													if (location.pathname === '/edit') {
														dispatch(
															updateStatus({
																value: true,
																data: { event, saveDraft: true }
															})
														);
														return;
													}

													if (
														location.pathname === '/new-esign'
														&& documentId
													) {
														event.preventDefault();
														searchParams.append('approval', true);
														setSearchParams(searchParams);
													}

													if (item.navigateTo === '/uploadfile') {
														if (
															location.pathname === '/new-esign'
															&& documentId
														) {
															event.preventDefault();
															searchParams.append('approval', true);
															setSearchParams(searchParams);
														} else {
															navigate('/new-esign');
														}
													} else {
														navigate(item.navigateTo);
													}
												}}
											/>
										);
									}

									if (item.title === 'Notification') {
										return null;
									}
								})}

								<Box display='flex' alignItems='center' gap={2}>
									{/* Button Group on the left */}
									<ButtonGroup
										sx={{
											borderRadius: '10px',
											overflow: 'hidden',
											border: '1px solid #b7d423'
										}}
									>
										<Button
											onClick={(event) => {
												handleTabChange(event, 1);
											}}
											sx={{
												...commonStyles,
												backgroundColor: isBusiness ? '#fff' : '#b7d423',
												color: 'black'
											}}
										>
      Individual
										</Button>
										<Button
											onClick={(event) => {
												handleTabChange(event, 0);
											}}
											sx={{
												...commonStyles,
												backgroundColor: isBusiness ? '#b7d423' : '#fff',
												color: 'black'
											}}
										>
      Business
										</Button>

									</ButtonGroup>
									{/* Avatar or Name Display */}
									{profileImage ? (
										<Avatar
											src={profileImage}
											className='avatar'
											onClick={handleClick}
											sx={{
												width: '40px',
												height: '40px',
												border: '2px solid #B7D423',
												position: 'relative',
												cursor: 'pointer'
											}}
										/>
									) : (
										<Box
											className='semiBold7'
											onClick={handleClick}
											style={{
												width: '40px',
												height: '40px',
												borderRadius: '20px',
												border: '2px solid #B7D423',
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												position: 'relative',
												cursor: 'pointer'
											}}
										>
											{name}
										</Box>
									)}
								</Box>
								{accountType !== 'individual' && <>
									{menuItems?.branding?.brandName ? (
										<Box
											className='medium4'
											sx={{
												display: 'flex',
												width: '130px',
												height: '40px',
												backgroundColor: '#ECF0E5',
												borderRadius: '5px',
												alignItems: 'center',
												justifyContent: 'center'
											}}
										>
											{brandName}
										</Box>
									) : (
										menuItems?.branding?.brandLogo && (
											<Avatar
												src={brandLogo || null}
												className='avatar'
												variant='square'
												sx={{
													width: '130px',
													height: '40px',
													position: 'relative'
												}}
											/>
										)
									)}
								</>}
							</Box>
							<RenderMenu
								anchorEl={anchorEl}
								open={open}
								handleClose={handleClose}
								handleLogout={handleLogout}
								handleResetPassword={handleResetPassword}
								itemMenu={itemMenu}
							/>

						</Toolbar>
					</AppBar>
				</Box>
			)}
			{openDigitalOrderPopup && (
				<DigitalStampOrder
					open={openDigitalOrderPopup}
					onClose={() => setOpenDigitalOrderPopup(false)}
				/>
			)}
		</>
	);
};

export default Header;

Header.propTypes = {
	menuItems: PropTypes.any
};
